import { d as colors } from "./focus-DaIphPOd.mjs";
import { c as transitions, h as fonts, l as radii, u as shadows } from "./motion-Cg-JnSSP.mjs";
import { forwardRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
//#region atoms/Input.tsx
/**
* EITELKEIT Input
* ═══════════════════════════════════════════════════════════════════
* Text input with mint focus glow - the signature EITELKEIT interaction
*/
const styles = {
	container: {
		display: "flex",
		alignItems: "center",
		gap: "12px",
		background: colors.surface,
		border: `1px solid ${colors.border}`,
		borderRadius: radii.md,
		transition: transitions.all,
		width: "100%"
	},
	containerFocus: {
		borderColor: colors.mint,
		boxShadow: shadows.focusRing
	},
	containerError: {
		borderColor: colors.danger,
		boxShadow: "0 0 0 3px " + colors.dangerGlow
	},
	input: {
		flex: 1,
		background: "transparent",
		border: "none",
		color: colors.textPrimary,
		fontFamily: fonts.sans,
		outline: "none",
		width: "100%"
	},
	placeholder: { color: colors.textMuted },
	sizes: {
		sm: {
			container: { padding: "8px 12px" },
			input: { fontSize: "12px" }
		},
		md: {
			container: { padding: "12px 16px" },
			input: { fontSize: "14px" }
		},
		lg: {
			container: { padding: "14px 18px" },
			input: { fontSize: "16px" }
		}
	},
	element: {
		display: "flex",
		alignItems: "center",
		color: colors.textMuted,
		flexShrink: 0
	},
	disabled: {
		opacity: .5,
		cursor: "not-allowed"
	}
};
const Input = forwardRef(({ size, error, leftElement, rightElement, disabled, style, onFocus, onBlur, ...props }, ref) => {
	const [isFocused, setIsFocused] = useState(false);
	const s = size ?? "md";
	const err = error ?? false;
	const handleFocus = (e) => {
		setIsFocused(true);
		onFocus?.(e);
	};
	const handleBlur = (e) => {
		setIsFocused(false);
		onBlur?.(e);
	};
	const containerStyle = {
		...styles.container,
		...styles.sizes[s].container,
		...isFocused && !err ? styles.containerFocus : {},
		...err ? styles.containerError : {},
		...disabled ? styles.disabled : {},
		...style
	};
	const inputStyle = {
		...styles.input,
		...styles.sizes[s].input
	};
	return /* @__PURE__ */ jsxs("div", {
		style: containerStyle,
		children: [
			leftElement && /* @__PURE__ */ jsx("span", {
				style: styles.element,
				children: leftElement
			}),
			/* @__PURE__ */ jsx("input", {
				ref,
				disabled,
				style: inputStyle,
				onFocus: handleFocus,
				onBlur: handleBlur,
				...props
			}),
			rightElement && /* @__PURE__ */ jsx("span", {
				style: styles.element,
				children: rightElement
			})
		]
	});
});
Input.displayName = "Input";
//#endregion
//#region atoms/Kbd.tsx
function Kbd({ size = "sm", style, children, ...props }) {
	return /* @__PURE__ */ jsx("kbd", {
		style: {
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			fontFamily: fonts.mono,
			fontSize: size === "sm" ? "9px" : "11px",
			fontWeight: 500,
			padding: size === "sm" ? "2px 4px" : "3px 6px",
			background: colors.surface,
			border: `1px solid ${colors.border}`,
			borderRadius: "3px",
			color: colors.textMuted,
			boxShadow: "inset 0 -1px 0 rgba(0,0,0,0.2)",
			...style
		},
		...props,
		children
	});
}
//#endregion
//#region atoms/StatusDot.tsx
const statusColors = {
	live: colors.mint,
	online: colors.mint,
	active: colors.mint,
	offline: colors.textMuted,
	away: colors.warning,
	idle: colors.warning,
	busy: colors.danger,
	error: colors.danger,
	waiting: colors.info
};
const sizeMap = {
	sm: 6,
	md: 8,
	lg: 10
};
function StatusDot({ status, size, pulse, ringPulse, label, style, ...props }) {
	const st = status ?? "online";
	const sz = size ?? "md";
	const pl = pulse ?? true;
	const rp = ringPulse ?? false;
	const s = sizeMap[sz];
	const color = statusColors[st] || colors.textMuted;
	const isActive = [
		"live",
		"online",
		"active"
	].includes(st);
	const shouldPulse = pl && isActive;
	const shouldRingPulse = rp && isActive;
	return /* @__PURE__ */ jsxs("span", {
		style: {
			display: "inline-flex",
			alignItems: "center",
			gap: "6px",
			...style
		},
		...props,
		children: [
			/* @__PURE__ */ jsxs("span", {
				style: {
					position: "relative",
					width: s,
					height: s
				},
				children: [shouldRingPulse && /* @__PURE__ */ jsx("span", { style: {
					position: "absolute",
					inset: -4,
					borderRadius: "50%",
					background: color,
					opacity: .3,
					animation: "e-ring-pulse 2s ease-in-out infinite"
				} }), /* @__PURE__ */ jsx("span", { style: {
					display: "block",
					width: "100%",
					height: "100%",
					borderRadius: "50%",
					background: color,
					animation: shouldPulse && !shouldRingPulse ? "e-dot-pulse 2s ease-in-out infinite" : "none",
					boxShadow: isActive ? `0 0 8px ${color}40` : "none"
				} })]
			}),
			label && /* @__PURE__ */ jsx("span", {
				style: {
					fontFamily: fonts.sans,
					fontSize: "12px",
					color: colors.textSecondary
				},
				children: label
			}),
			/* @__PURE__ */ jsx("style", { children: `
        @keyframes e-dot-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes e-ring-pulse {
          0% { transform: scale(1); opacity: 0.4; }
          50% { transform: scale(1.5); opacity: 0; }
          100% { transform: scale(1); opacity: 0.4; }
        }
      ` })
		]
	});
}
//#endregion
export { Kbd as n, Input as r, StatusDot as t };

//# sourceMappingURL=StatusDot-CP0x3IGI.mjs.map